import React from 'react';
import useSWRInfinite from 'swr/infinite';
import { SortHandler, SortRule } from '@economic/taco';
import { CollectionEnvelope } from '~/types/internal-api';
import usePagePreferences from './usePagePreferences';

import { fetcher, getDefaultSortRules, createPath, CollectionQueryPreferences } from './useCollectionQuery';

const DEFAULT_PAGE_SIZE = 50;

export interface InfiniteCollectionQuery<TType = unknown> {
    data: TType[];
    error?: Error;
    loading: boolean;
    refetch: () => void;
    totalUnfilteredLength: number;
    length: number;
    sorting: {
        onSort: SortHandler<TType>;
        sortRules: SortRule<TType>[];
    };
    loadMore: (startRowIndex?: number, endRowIndex?: number) => void;
}

export const useInfiniteCollectionQuery = <TType = unknown>(
    endpoint: string,
    pageSize = DEFAULT_PAGE_SIZE,
    uniqueId = ''
): InfiniteCollectionQuery<TType> => {
    // prevent querying multiple times for quick scrolling by storing the last page outside of state
    const lastRequestedPageNumber = React.useRef(1);
    const [pagePrefs, setPagePrefs] = usePagePreferences<CollectionQueryPreferences>(uniqueId);

    // initialise state from endpoint, incase sorting and pagination are set as defaults or are saved in page prefs
    const [sortRules, setSortRules] = React.useState<SortRule<TType>[]>(getDefaultSortRules(endpoint, pagePrefs));

    // perform query
    const response = useSWRInfinite<CollectionEnvelope<TType>>(
        pageIndex => createPath(endpoint, sortRules, { pageIndex, pageSize }, false),
        fetcher,
        {
            revalidateOnFocus: false, // causes strange double queries when unmounting
        }
    );

    const loadMore = (_, endRowIndex: number | undefined) => {
        if (response.isValidating || (!response.data && !response.error)) return;
        // prevent refeching if the last resolved request contains less data than requests
        if (response.data && response.data[response.data.length - 1].data.length < pageSize) return;

        let pageNumber: number;

        if (endRowIndex) {
            pageNumber = Math.ceil((endRowIndex + 1) / pageSize);
        } else {
            pageNumber = response.size + 1;
        }

        if (pageNumber > lastRequestedPageNumber.current) {
            lastRequestedPageNumber.current = pageNumber;
            response.setSize(pageNumber);
        }
    };

    const refetch = React.useCallback(() => {
        lastRequestedPageNumber.current = 1;
        response.mutate();
    }, []);

    React.useEffect(() => {
        // Reset lastRequestedPageNumber when retriggering retrieval of data
        lastRequestedPageNumber.current = 1;
    }, [JSON.stringify(sortRules), endpoint]);

    return {
        data: response.data?.flatMap(page => page.data || []) || [],
        error: response.error,
        loading: response.isValidating || (!response.data && !response.error),
        refetch,
        totalUnfilteredLength: response.data?.[0]?.totalUnfilteredCount ?? 0,
        length: response.data?.[0]?.pagination?.count ?? 0,
        sorting: {
            onSort: (nextSortRules = []) => {
                setSortRules(nextSortRules);
                // save rules to local storage
                setPagePrefs({ sortRules: nextSortRules });
            },
            sortRules,
        },
        loadMore,
    };
};
